import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { PluginListenerHandle } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

export interface NativeKeyboardContextValue {
  /** Native keyboard height in CSS pixels; 0 when hidden. */
  keyboardInsetPx: number;
  isKeyboardVisible: boolean;
  /** True when one or more active consumers want the floating dismiss button hidden. */
  isDismissButtonSuppressed: boolean;
  /** Increment the suppression counter. */
  suppressDismissButton: () => void;
  /** Decrement the suppression counter (matched call to suppressDismissButton). */
  unsuppressDismissButton: () => void;
}

const NativeKeyboardContext = createContext<NativeKeyboardContextValue | null>(null);

export const NativeKeyboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [keyboardInsetPx, setKeyboardInsetPx] = useState(0);
  const [dismissSuppressCount, setDismissSuppressCount] = useState(0);

  const suppressDismissButton = useCallback(() => {
    setDismissSuppressCount(c => c + 1);
  }, []);
  const unsuppressDismissButton = useCallback(() => {
    setDismissSuppressCount(c => Math.max(0, c - 1));
  }, []);

  useEffect(() => {
    // Set initial value so consumers don't need to fall back.
    try {
      document.documentElement.style.setProperty('--keyboard-inset', '0px');
    } catch { /* noop */ }

    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let cancelled = false;
    const registered: PluginListenerHandle[] = [];

    // Always mirror the keyboard inset to a CSS variable on <html>. Components
    // that consume keyboard height should prefer this CSS var over React state
    // when possible — iOS fires keyboard events many times per keystroke
    // (predictive bar updates) and React re-renders during typing disrupt the
    // active text-input session, killing key-repeat acceleration / caps state.
    const setCssVar = (px: number) => {
      try {
        document.documentElement.style.setProperty('--keyboard-inset', `${px}px`);
      } catch {
        /* noop */
      }
    };

    const register = async () => {
      try {
        const track = async (handle: Promise<PluginListenerHandle>) => {
          const h = await handle;
          if (cancelled) {
            void h.remove();
            return;
          }
          registered.push(h);
        };

        await track(
          Keyboard.addListener('keyboardWillShow', (info) => {
            if (cancelled || info.keyboardHeight <= 0) return;
            setCssVar(info.keyboardHeight);
            setKeyboardInsetPx(info.keyboardHeight);
          })
        );
        await track(
          Keyboard.addListener('keyboardDidShow', (info) => {
            if (cancelled || info.keyboardHeight <= 0) return;
            setCssVar(info.keyboardHeight);
            setKeyboardInsetPx(info.keyboardHeight);
          })
        );
        const onHide = () => {
          if (cancelled) return;
          setCssVar(0);
          setKeyboardInsetPx(0);
        };
        await track(Keyboard.addListener('keyboardWillHide', onHide));
        await track(Keyboard.addListener('keyboardDidHide', onHide));
      } catch (e) {
        console.warn('NativeKeyboardProvider: listener setup failed', e);
      }
    };

    void register();

    return () => {
      cancelled = true;
      registered.forEach((h) => void h.remove());
    };
  }, []);

  const value = useMemo<NativeKeyboardContextValue>(
    () => ({
      keyboardInsetPx,
      isKeyboardVisible: keyboardInsetPx > 0,
      isDismissButtonSuppressed: dismissSuppressCount > 0,
      suppressDismissButton,
      unsuppressDismissButton,
    }),
    [keyboardInsetPx, dismissSuppressCount, suppressDismissButton, unsuppressDismissButton]
  );

  return <NativeKeyboardContext.Provider value={value}>{children}</NativeKeyboardContext.Provider>;
};

export function useNativeKeyboard(): NativeKeyboardContextValue {
  const ctx = useContext(NativeKeyboardContext);
  if (!ctx) {
    return {
      keyboardInsetPx: 0,
      isKeyboardVisible: false,
      isDismissButtonSuppressed: false,
      suppressDismissButton: () => undefined,
      unsuppressDismissButton: () => undefined,
    };
  }
  return ctx;
}

/**
 * Hide the global NativeKeyboardDismissButton while this hook is mounted with `active=true`.
 * Use in dialogs whose own UI sits where the floating dismiss icon would render — e.g. the
 * comment composer's Send button at bottom-right.
 *
 * Counter-based, so nested suppressors compose correctly.
 */
export function useSuppressKeyboardDismissButton(active: boolean): void {
  const { suppressDismissButton, unsuppressDismissButton } = useNativeKeyboard();
  useEffect(() => {
    if (!active) return;
    suppressDismissButton();
    return () => unsuppressDismissButton();
  }, [active, suppressDismissButton, unsuppressDismissButton]);
}

export async function dismissNativeKeyboard(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  try {
    await Keyboard.hide();
  } catch (e) {
    console.warn('dismissNativeKeyboard:', e);
  }
}
